import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { adminApi, type AdminUser } from "@/api/admin";
import { ApiError } from "@/api/client";

export default function UsersPage() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState<AdminUser[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .listUsers(debouncedQ, null)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setCursor(res.next_cursor);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ]);

  const loadMore = () => {
    if (!cursor || loading) return;
    setLoading(true);
    adminApi
      .listUsers(debouncedQ, cursor)
      .then((res) => {
        setItems((prev) => [...prev, ...res.items]);
        setCursor(res.next_cursor);
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索 open_id 或姓名…"
          className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-sm outline-none focus:border-sky-400"
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">用户</th>
              <th className="px-3 py-2">open_id</th>
              <th className="px-3 py-2 text-right">收藏数</th>
              <th className="px-3 py-2">注册时间</th>
              <th className="px-3 py-2">refresh 到期</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2">
                  <Link
                    to={`/admin/users/${encodeURIComponent(u.open_id)}`}
                    className="flex items-center gap-2 text-slate-900 hover:underline"
                  >
                    {u.avatar_url ? (
                      <img
                        src={u.avatar_url}
                        alt=""
                        className="h-6 w-6 rounded-full"
                      />
                    ) : (
                      <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-200 text-[10px]">
                        {(u.name || "?").slice(0, 1)}
                      </span>
                    )}
                    <span>{u.name || "（未命名）"}</span>
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                  {u.open_id}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {u.favorites_count}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {new Date(u.created_at).toLocaleDateString("zh-CN")}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {new Date(u.refresh_expires_at).toLocaleDateString("zh-CN")}
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                  无匹配用户
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {cursor ? (
        <div className="text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600 disabled:opacity-50"
          >
            {loading ? "加载中…" : "加载更多"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
